<template>
  <v-card class="group-by-views" variant="flat" color="transparent">
    <v-toolbar v-if="!hideToolbar" dense flat color="transparent" style="margin-bottom: -16px;" >
      <v-toolbar-title class="font-weight-bold">Stats</v-toolbar-title>
      <v-spacer/>
      <action-menu v-if="['works', 'awards'].includes(entityType)" class="ml-2" action="group_by"/>
      <v-btn icon :href="csvUrl" aria-label="Export as CSV">
        <v-icon color="grey-darken-2">mdi-tray-arrow-down</v-icon>
      </v-btn>
      <v-menu location="bottom" v-if="0">
        <template v-slot:activator="{props}">
          <v-btn icon v-bind="props">
            <v-icon color="grey-darken-2">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="url.setGroupBy(undefined)">
            <template #prepend>
              <v-icon color="grey-darken-2">mdi-restore</v-icon>
            </template>
            <v-list-item-title>Restore report defaults</v-list-item-title>
          </v-list-item>

          <v-divider/>

          <v-list-item :href="csvUrl">
            <template #prepend>
              <v-icon color="grey-darken-2">mdi-tray-arrow-down</v-icon>
            </template>
            <v-list-item-title>Export all</v-list-item-title>
            <v-list-item-subtitle>.csv</v-list-item-subtitle>
          </v-list-item>

          <v-list-item @click="store.commit('openApiDialog', apiUrl)">
            <template #prepend>
              <v-icon color="grey-darken-2">mdi-api</v-icon>
            </template>
            <v-list-item-title>View in API</v-list-item-title>
            <v-list-item-subtitle>.json</v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar>
    
    <v-container class="pt-0">
      <v-row v-if="resultsObject?.meta?.count" dense class="">
        <!-- Compact rail (#440 r6): widgets drag-to-reorder via the grip in each
             card's header. Handle pattern: grip mousedown arms this column's
             draggable; the column is the drop target (works across columns in
             2-col mode — order is the flattened array order). Drop rewrites
             ?group_by=. The apc_sum/cited_by_count_sum special cards are drop
             targets but (grip-less) not draggable. -->
        <v-col
            v-for="(key, i) in groupByKeys"
            :key="key"
            :cols="colSize"
            class="d-flex flex-column"
            :class="{ 'col-drop-target': compact && dragKey && dragOverKey === key && dragKey !== key }"
            :draggable="compact && armedKey === key ? true : undefined"
            @dragstart="onColDragStart(key, $event)"
            @dragover="compact && dragKey ? onColDragOver(key, $event) : null"
            @drop="compact ? onColDrop(key, $event) : null"
            @dragend="onColDragEnd"
        >
          <template v-if="i === 0 && !hideResultsCount">
            <v-card variant="outlined" class="bg-white pa-3 mb-3">
              <serp-results-count :results-object="resultsObject" class="text-h5"/>
            </v-card>
          </template>

          <v-card variant="outlined" v-if="key === 'apc_sum'" class="bg-white">
            <v-toolbar flat>
              <v-icon color="grey-darken-2" start>mdi-currency-usd</v-icon>
              <v-toolbar-title>APC sums</v-toolbar-title>
              <v-spacer/>
              <v-btn icon @click="url.toggleGroupBy('apc_sum')">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-divider/>
            <div class="ma-4">
              <div class="text-h5">
                <span class="font-weight-bold mr-2">${{ filters.toPrecision(resultsObject?.meta?.apc_paid_sum_usd) }}</span>
              </div>
              <div class="text-body-2">Sum APCs paid (est)</div>
              <div class="mt-3">
                <span class="font-weight-bold mr-2">${{ filters.toPrecision(resultsObject?.meta?.apc_list_sum_usd) }}</span>
              </div>
              <div class="text-body-2">Sum APCs list (est)</div>
            </div>
          </v-card>

          <v-card variant="outlined" v-else-if="key === 'cited_by_count_sum'" class="d-flex align-baseline pa-2 bg-white">
            <v-icon color="grey-darken-2" start>mdi-format-quote-close</v-icon>
            <span class="text-h5 mr-2">{{ filters.toPrecision(resultsObject?.meta?.cited_by_count_sum) }}</span>
            <div class="align-self-baseline ">citations</div>
            <v-spacer/>
            <v-btn icon @click="url.toggleGroupBy('cited_by_count_sum')">
              <v-icon color="grey-darken-2">mdi-close</v-icon>
            </v-btn>
          </v-card>

          <group-by
            v-else
            :filter-key="key"
            :entity-type="entityType"
            :hide-more="hideMore"
            :compact="compact"
            @grip-down="armDrag(key)"
          />

        </v-col>

      </v-row>
      <v-card v-else variant="outlined" class="text-grey mt-2 pa-4 bg-white">
        There are no results to analyze.
      </v-card>

    </v-container>
  </v-card>
</template>


<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import { url } from '@/url';
import filters from '@/filters';
import { filtersFromUrlStr } from '@/filterConfigs';

import GroupBy from '@/components/GroupBy/GroupBy.vue';
import ActionMenu from '@/components/Action/ActionMenu.vue';
import SerpResultsCount from '@/components/SerpResultsCount.vue';

defineOptions({ name: 'GroupByViews'});

// Props
const props = defineProps({
  resultsObject: Object,
  hideToolbar: Boolean,
  hideResultsCount: Boolean,
  hideMore: Boolean,
  // When true, force every group-by widget into ONE column (cols=12) instead of
  // Vuetify's default 12-col auto-flow (1/2/3 cols). Used by GroupBySidebar (the
  // flag-on left rail); the flag-off right-column path leaves this false/undefined.
  singleColumn: Boolean,
  // Explicit fixed column count (1 or 2) for the flag-on rail's two-column toggle.
  // Wins over singleColumn; null/undefined keeps Vuetify's reflow (flag-off path).
  columns: { type: Number, default: null },
  // Flag-on rail's denser widget styling (#440 round 5): compact OA donut, fixed-
  // bin year histogram + range slider, rounded-square header buttons. Flag-off
  // (ExpertSerp) never passes this, so the legacy widgets are untouched.
  compact: Boolean,
});

// Store and router
const store = useStore();
const route = useRoute();

const entityType = computed(() => store.getters.entityType);

// Column sizing: explicit `columns` (1|2) → cols=12/columns; else singleColumn →
// cols=12; else undefined (Vuetify auto-flow, the flag-off reflow path).
const colSize = computed(() => {
  if (props.columns) return Math.floor(12 / props.columns);
  if (props.singleColumn) return 12;
  return undefined;
});

// Group by keys. Flag-off: money widgets (apc_sum/cited_by_count_sum) pinned
// first, as always. Compact rail: respect the raw URL order — drag-to-reorder
// owns it (#440 r6), so no pin-first sort.
const groupByKeys = computed(() => {
  const keys = url.getGroupBy(route);
  if (!props.compact) {
    keys.sort((a) => ['apc_sum', 'cited_by_count_sum'].includes(a) ? -1 : 1);
  }
  return keys;
});

// ---- widget drag-to-reorder (compact rail, #440 r6) -------------------------
// Same native HTML5 DnD as ColumnEditorPanel's chips, with the handle pattern:
// only a grip-armed column is draggable, so text selection / accidental drags
// elsewhere on the card stay normal.
const armedKey = ref(null);   // grip mousedown → this widget's column is draggable
const dragKey = ref(null);    // actively dragging
const dragOverKey = ref(null);

function armDrag(key) {
  armedKey.value = key;
}
function disarmDrag() {
  if (!dragKey.value) armedKey.value = null;
}
onMounted(() => window.addEventListener('mouseup', disarmDrag));
onBeforeUnmount(() => window.removeEventListener('mouseup', disarmDrag));

function onColDragStart(key, e) {
  if (!props.compact || armedKey.value !== key) {
    e.preventDefault();
    return;
  }
  dragKey.value = key;
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs data set for the drag to fire.
    e.dataTransfer.setData('text/plain', key);
  }
}
function onColDragOver(key, e) {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  dragOverKey.value = key;
}
function onColDrop(targetKey, e) {
  e.preventDefault();
  const from = dragKey.value;
  dragOverKey.value = null;
  if (!from || from === targetKey) return;
  const keys = url.getGroupBy(route);
  const fromIdx = keys.indexOf(from);
  const toIdx = keys.indexOf(targetKey);
  if (fromIdx < 0 || toIdx < 0) return;
  keys.splice(fromIdx, 1);
  keys.splice(toIdx, 0, from);
  url.setGroupBy(keys);
  dragKey.value = null;
  armedKey.value = null;
}
function onColDragEnd() {
  dragKey.value = null;
  dragOverKey.value = null;
  armedKey.value = null;
}


// API URL based on current filters
const apiUrl = computed(() => {
  const myFilters = filtersFromUrlStr(entityType.value, route.query.filter);
  return url.makeGroupByUrl(
    entityType.value,
    groupByKeys.value.join(','),
    {
      filters: myFilters,
      isMultipleGroups: true
    }
  );
});

// CSV export URL
const csvUrl = computed(() => {
  const myFilters = filtersFromUrlStr(entityType.value, route.query.filter);
  return url.makeGroupByUrl(
    entityType.value,
    groupByKeys.value.join(','),
    {
      filters: myFilters,
      isMultipleGroups: true,
      formatCsv: true
    }
  );
});
</script>


<style scoped lang="scss">
.group-by-views .v-toolbar__content {
  padding-left: 0 !important;
}
.group-by-views .v-toolbar__content > .v-toolbar-title {
  margin-inline-start: 16px !important;
}

/* Drag-to-reorder insertion indicator (compact rail, #440 r6): a short bar
   above the column the dragged widget would land on. */
.col-drop-target {
  position: relative;
}
.col-drop-target::before {
  content: '';
  position: absolute;
  left: 8px;
  right: 8px;
  top: -8px;
  height: 4px;
  border-radius: 2px;
  background: #000; /* r7: was 35% gray — too subtle */
}
</style>